import { Formik, Form, Field, FormikProps } from "formik";
import FormInput from "@/shared/components/ui/FormInput/FormInput";
import { Button } from "@/shared/components/ui/Button/Button";
import { Switch } from "@/shared/components/ui/Switch/Switch";
import { FormSelect } from "@/shared/components/ui/FormSelect/FormSelect";
import {
  InputContainer,
  StyledHr,
  ButtonContainer,
  SwitchContainer,
  ModalContent,
  SwitchMessage,
  SwitchTitle,
  ModalBackdrop,
  FormContainer,
  SignatureFieldContainer,
  ResponsiveModalTemplate,
} from "./UserDataModal.styles";
import { validationSchema } from "./schema";
import { SignaturePad } from "@/shared/components/ui/SignaturePad";
import { useUserDataModal } from "./useUserDataModal";
import {
  toggleHealthProfessional,
  USER_DATA_INITIAL_VALUES,
  type UserDataFormValues,
} from "./userDataForm";

const parseSelectValue = (value: string | string[]): number => {
  const selectedValue = Array.isArray(value) ? value[0] : value;

  return Number.parseInt(selectedValue, 10);
};

const UserDataModal = () => {
  const {
    closeModal,
    handleSubmit,
    isPending,
    professionOptions,
    registrationTypeOptions,
  } = useUserDataModal();

  const onSelectChange =
    (
      fieldName: string,
      setFieldValue: (field: string, value: unknown) => void,
    ) =>
    (value: string | string[]) => {
      setFieldValue(fieldName, parseSelectValue(value));
    };

  return (
    <>
      <ModalBackdrop />
      <ResponsiveModalTemplate onClose={closeModal} showCloseButton={false}>
        <ModalContent>
          <p>
            Para brindarte la mejor experiencia en Emergencias | Digital Health,
            te pedimos que registres tus datos una única vez.
          </p>

          <Formik
            initialValues={USER_DATA_INITIAL_VALUES}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnMount
          >
            {({
              setFieldValue,
              setValues,
              errors,
              touched,
              isValid,
              values,
            }: FormikProps<UserDataFormValues>) => {
              return (
                <Form>
                  <InputContainer>
                    <Field
                      id="name"
                      name="name"
                      label="Nombre/s"
                      as={FormInput}
                      error={(touched.name && errors.name) || ""}
                    />

                    <Field
                      id="surname"
                      name="surname"
                      label="Apellido/s"
                      as={FormInput}
                      error={(touched.surname && errors.surname) || ""}
                    />

                    <SwitchContainer>
                      <div>
                        <SwitchTitle htmlFor="health_professional">
                          Soy profesional de la salud
                        </SwitchTitle>
                        <Switch
                          id="health_professional"
                          isChecked={values.health_professional}
                          onChange={() => {
                            setValues(toggleHealthProfessional(values));
                          }}
                        />
                      </div>

                      <SwitchMessage>
                        Si tu profesión o tipo de matrícula no aparecen en la
                        lista, podes continuar desmarcando esta opción.
                      </SwitchMessage>
                    </SwitchContainer>

                    {values.health_professional && (
                      <FormContainer>
                        <Field
                          name="id_profession"
                          id="id_profession"
                          label="Profesión"
                          options={professionOptions}
                          value={
                            values.id_profession
                              ? values.id_profession.toString()
                              : ""
                          }
                          onChange={onSelectChange(
                            "id_profession",
                            setFieldValue,
                          )}
                          error={
                            touched.id_profession && !values.id_profession
                              ? "Selecciona una opción"
                              : undefined
                          }
                          as={FormSelect}
                        />

                        <Field
                          name="id_registration_type"
                          id="id_registration_type"
                          label="Tipo de matrícula"
                          options={registrationTypeOptions}
                          value={
                            values.id_registration_type
                              ? values.id_registration_type.toString()
                              : ""
                          }
                          onChange={onSelectChange(
                            "id_registration_type",
                            setFieldValue,
                          )}
                          error={
                            touched.id_registration_type &&
                            !values.id_registration_type
                              ? "Selecciona una opción"
                              : undefined
                          }
                          as={FormSelect}
                          translate="no"
                        />

                        <Field
                          id="registration_number"
                          name="registration_number"
                          label="Número de matrícula"
                          as={FormInput}
                          error={
                            touched.registration_number
                              ? errors.registration_number
                              : ""
                          }
                        />

                        <Field
                          id="document_number"
                          name="document_number"
                          label="CUIT"
                          as={FormInput}
                          error={
                            touched.document_number
                              ? errors.document_number
                              : ""
                          }
                        />
                        <SignatureFieldContainer>
                          <SignaturePad
                            onChange={(file) => {
                              setFieldValue("signature", file);
                            }}
                          />
                        </SignatureFieldContainer>
                      </FormContainer>
                    )}
                  </InputContainer>

                  <StyledHr />

                  <ButtonContainer>
                    <Button
                      type="submit"
                      disabled={!isValid || isPending}
                      variant="primary"
                      size="lg"
                    >
                      Ingresar
                    </Button>
                  </ButtonContainer>
                </Form>
              );
            }}
          </Formik>
        </ModalContent>
      </ResponsiveModalTemplate>
    </>
  );
};

export default UserDataModal;
